import { URL_PREFIX } from '../constants/api';
import { HIST_RANGE } from '../constants/label-perf';
import type {
  FixesHistResponseValue,
  FixesHistValue,
  HistRange,
  LabelPerfModel,
  LabelPerfResponse,
  LabelResponse,
  MixOnePerformanceResp,
  MonthlyContrib,
  MonthlyContribResponse,
  PerformanceStats,
  TopTenOrder,
  TopTenType,
  TrackItem,
  TrackPerformanceItem,
  TrackPerformanceResponse,
  TrackResponse,
} from '../types/label-perf';
import { reduceHistRange } from '../utils/hist';
import { mapTrackResponse } from '../utils/track';
import { APIError } from './errors';

export const getLabelPerfReport = async (
  majorLabels: string[],
  wmg1Labels?: number[],
): Promise<LabelPerfModel> => {
  try {
    const url = `${URL_PREFIX}/api/v1/exec-view/kpi-report`;

    const payload: { major_labels?: string[]; wmg1_labels?: number[] } = {};

    if (wmg1Labels && wmg1Labels.length > 0) {
      payload.wmg1_labels = wmg1Labels;
      payload.major_labels = [];
    } else {
      payload.major_labels = majorLabels;
    }

    const body = JSON.stringify(payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch report: ${response.statusText}`,
        response.status,
      );
    }

    const r = (await response.json()) as LabelPerfResponse;

    const labelPerf: LabelPerfModel = {
      totalAssets: r.tracks,
      totalGaps: r.tracks_need_review,
      overallMonthlyRevenue: r.monthly_revenue,
      overallMonthlyRevenueChange: r.monthly_revenue_trend,
      overallMonthlyStreams: r.monthly_streams,
      overallMonthlyStreamsChange: r.monthly_streams_trend,
      opportunityRevenueFromFixes: r.opportunity_revenue_from_fixes,
      revenueFromFixes: r.monthly_revenue_from_fixes,
      revenueFromFixesChange: r.monthly_revenue_trend_from_fixes,
      streamsFromFixes: r.monthly_streams_from_fixes,
      streamsFromFixesChange: r.monthly_streams_trend_from_fixes,
    };

    return labelPerf;
  } catch (error) {
    console.warn('Failed to get report:', error);

    throw error;
  }
};

export const getFixesHistory = async (
  majorLabels: string[],
  range: HistRange,
  wmg1Labels?: number[],
): Promise<FixesHistValue[]> => {
  try {
    const url = `${URL_PREFIX}/api/v1/exec-view/fixes-history`;

    const payload: {
      major_labels?: string[];
      wmg1_labels?: number[];
      days: number;
    } = {
      days: HIST_RANGE[range],
    };

    if (wmg1Labels && wmg1Labels.length > 0) {
      payload.wmg1_labels = wmg1Labels;
      payload.major_labels = [];
    } else {
      payload.major_labels = majorLabels;
    }

    const body = JSON.stringify(payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch report: ${response.statusText}`,
        response.status,
      );
    }

    const r = (await response.json()) as FixesHistResponseValue[];

    const fixesHistValues: FixesHistValue[] = r.map((value) => ({
      name: value.date,
      inReview: value.active_track_locks,
      fixed: value.fixed_tracks,
      needFixes: value.tracks_to_investigate,
    }));

    return reduceHistRange(fixesHistValues, range);
  } catch (error) {
    console.warn('Failed to get fixes history:', error);

    throw error;
  }
};

export const getMonthlyContrib = async (
  majorLabels: string[],
  months = 12,
  wmg1Labels?: number[],
): Promise<MonthlyContrib[]> => {
  try {
    const url = `${URL_PREFIX}/api/v1/exec-view/fix-metrics`;

    const payload: {
      major_labels?: string[];
      wmg1_labels?: number[];
      months: number;
    } = {
      months,
    };

    if (wmg1Labels && wmg1Labels.length > 0) {
      payload.wmg1_labels = wmg1Labels;
      payload.major_labels = [];
    } else {
      payload.major_labels = majorLabels;
    }

    const body = JSON.stringify(payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch report: ${response.statusText}`,
        response.status,
      );
    }

    const r = (await response.json()) as MonthlyContribResponse[];

    return r.map((v) => ({
      date: new Date(v.date),
      totalStreams: v.total_streams,
      totalRevenue: v.total_revenue,
      fixesCount: v.total_fixes ?? 0,
    }));
  } catch (error) {
    console.warn('Failed to get monthly contribution:', error);

    throw error;
  }
};

export const getTopTenTracks = async (
  metric: TopTenType,
  order: TopTenOrder,
  majorLabels: string[],
  wmg1Labels?: number[],
): Promise<TrackItem[]> => {
  try {
    const url = `${URL_PREFIX}/api/v1/exec-view/top-ten`;

    const payload: {
      metric: string;
      order: string;
      major_labels?: string[];
      wmg1_labels?: number[];
    } = {
      metric: metric.toUpperCase(),
      order: order.toUpperCase(),
    };

    if (wmg1Labels && wmg1Labels.length > 0) {
      payload.wmg1_labels = wmg1Labels;
      payload.major_labels = [];
    } else {
      payload.major_labels = majorLabels;
    }

    const body = JSON.stringify(payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    const r = (await response.json()) as TrackResponse[];

    return r.map(mapTrackResponse);
  } catch (error) {
    console.warn('Failed to get top 10 tracks:', error);

    throw error;
  }
};

export const getLables = async (
  column: string,
  refinementFilter?: string,
): Promise<string[]> => {
  const url = `${URL_PREFIX}/api/v1/dashboard/refiners`;

  const body = JSON.stringify({
    column,
    ...(refinementFilter && {
      refinement_filter: `ILIKE '%${refinementFilter}%'`,
    }),
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
  });

  if (!response.ok) {
    const errorBody = await response.json();

    throw new APIError(
      errorBody.detail || 'Failed to fetch labels',
      response.status,
    );
  }

  const r = (await response.json()) as LabelResponse;

  return r.data;
};

export type VintageFilter =
  | 'NEW_RELEASE'
  | 'SHALLOW_CATALOG'
  | 'DEEP_CATALOG'
  | undefined;

export const getTrackPerformance = async (
  majorLabels: string[],
  order: string,
  limit: number,
  offset: number,
  wmg1Labels?: number[],
  vintage?: VintageFilter,
  mixOneEnrich?: boolean,
): Promise<TrackPerformanceItem[]> => {
  try {
    const url = `${URL_PREFIX}/api/v1/exec-view/track-performance`;

    const payload: {
      major_labels?: string[];
      wmg1_labels?: number[];
      order: string;
      limit: number;
      offset: number;
      vintage?: VintageFilter;
    } = {
      order,
      limit,
      offset,
      ...(vintage && { vintage }),
    };

    if (wmg1Labels && wmg1Labels.length > 0) {
      payload.wmg1_labels = wmg1Labels;
      payload.major_labels = [];
    } else {
      payload.major_labels = majorLabels;
    }

    const body = JSON.stringify(payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch track performance: ${response.statusText}`,
      );
    }

    const r = (await response.json()) as { data: TrackPerformanceResponse[] };

    const trackPerformanceItems: TrackPerformanceItem[] = r.data.map(
      (item) => ({
        familyId: item.family_id,
        familyTitle: item.family_title,
        familyDescription: item.family_description,
        isrcList: item.isrc_list,
        artist: {
          name: item.artist.name,
          uaid: item.artist.uaid,
          partyId: item.artist.party_id,
          familySplit: item.artist.family_split,
          formatCode: item.artist.format_code,
        },
        releaseDate: item.release_date,
        streamsRtd: item.streams_rtd,
        streamsTw: item.streams_tw,
        streamsLw: item.streams_lw,
        streamsWow: item.streams_wow,
        organicStreamsTw: item.organic_streams_tw,
        organicStreamsLw: item.organic_streams_lw,
        organicStreamsWow: item.organic_streams_wow,
        socialCreationsTw: item.social_creations_tw,
        socialCreationsLw: item.social_creations_lw,
        socialCreationsWow: item.social_creations_wow,
        pctStreamsAdSupported: item.pct_streams_ad_supported,
        pctStreamsPaid: item.pct_streams_paid,
        pctStreamsAmazon: item.pct_streams_amazon,
        pctStreamsApple: item.pct_streams_apple,
        pctStreamsSpotify: item.pct_streams_spotify,
        pctStreamsYoutube: item.pct_streams_youtube,
        pctStreamsOther: item.pct_streams_other,
        majorLabels: item.major_labels,
        relatedArtists: item.related_artists.map((artist) => ({
          name: artist.name,
          uaid: artist.uaid,
          partyId: artist.party_id,
        })),
      }),
    );

    if (mixOneEnrich) {
      // Enrich with MixOne data if the feature flag is enabled
      const familyIds = trackPerformanceItems.map((item) => item.familyId);
      const familyIdStats = await getFamilyIdStats(familyIds);

      trackPerformanceItems.forEach((item) => {
        const pstats = familyIdStats[item.familyId];

        if (pstats) {
          item.streamsRtd = pstats.streamsRtd;
          item.streamsTw = pstats.streamsTw;
          item.streamsLw = pstats.streamsLw;
          item.streamsWow = pstats.streamsWow;
          item.organicStreamsTw = pstats.organicStreamsTw;
          item.organicStreamsLw = pstats.organicStreamsLw;
          item.organicStreamsWow = pstats.organicStreamsWow;
          item.socialCreationsTw = pstats.socialCreationsTw;
          item.socialCreationsLw = pstats.socialCreationsLw;
          item.socialCreationsWow = pstats.socialCreationsWow;
        }
      });

      // Reapply client side sorting
      if (order && ['-streams_tw', '-streams_rtd'].includes(order)) {
        trackPerformanceItems.sort((a, b) => {
          const aValue = order === '-streams_tw' ? a.streamsTw : a.streamsRtd;
          const bValue = order === '-streams_tw' ? b.streamsTw : b.streamsRtd;

          return bValue - aValue; // Sort desc
        });
      }
    }

    return trackPerformanceItems;
  } catch (error) {
    console.warn('Failed to get track performance:', error);

    throw error;
  }
};

// MixOne Enrich API for Family ID Performance

const getFamilyIdStats = async (
  familyIDs: string[],
  period: 'weekly' | 'monthly' = 'weekly',
  sortBy = 'release_date',
): Promise<{ [familyId: string]: PerformanceStats }> => {
  const MIXONE_API_PREFIX = '/api/mixone';
  const result: { [familyId: string]: PerformanceStats } = {};

  try {
    const page = 0;
    const pageSize = 20;
    const url = `${MIXONE_API_PREFIX}/api/v1/tracks/performance/${familyIDs.join(',')}?period=${period}&sort_by=${sortBy}&page=${page}&page_size=${pageSize}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch family ID stats: ${response.statusText}`,
      );
    }

    const data: MixOnePerformanceResp = await response.json();

    if (!data || !data.tracks) {
      return result;
    }

    data.tracks.forEach((track) => {
      const familyId = parseInt(track.family_id, 10);
      const streamingByDsp = track.streaming_by_dsp[0];
      const engagementByDsp = track.engagement_by_dsp[0];

      const streamsTw = streamingByDsp.this_period_streams;
      const streamsLw = streamingByDsp.previous_period_streams;
      const organicStreamsTw = streamingByDsp.this_period_organic_streams;
      const organicStreamsLw = streamingByDsp.previous_period_organic_streams;
      const socialCreationsTw = engagementByDsp.this_period_social_creations;
      const socialCreationsLw =
        engagementByDsp.previous_period_social_creations;

      result[familyId] = {
        streamsRtd: streamingByDsp.total_streams,
        streamsTw,
        streamsLw,
        streamsWow: streamsTw
          ? Math.round((((streamsTw - streamsLw) * 100) / streamsLw) * 100) /
            100
          : 0,
        organicStreamsTw,
        organicStreamsLw,
        organicStreamsWow: organicStreamsTw
          ? Math.round(
              (((organicStreamsTw - organicStreamsLw) * 100) /
                organicStreamsLw) *
                100,
            ) / 100
          : 0,
        socialCreationsTw,
        socialCreationsLw,
        socialCreationsWow: socialCreationsTw
          ? Math.round(
              (((socialCreationsTw - socialCreationsLw) * 100) /
                socialCreationsLw) *
                100,
            ) / 100
          : 0,
      };
    });

    return result;
  } catch (error) {
    console.warn('Failed to get family ID stats:', error);

    throw error;
  }
};
